import { Injectable, inject } from '@angular/core';
import { ActivationEnd, Router } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { ProductNames } from '@mynexus/mynexus-ui-lib';
import {
  GlobalBroadcastService,
  IMfeService,
  ISharedServices,
  MessageCallback
} from '@mynexus/mfe-core';
import { PrimeNGConfigType } from 'primeng/config';
import { Carelon, CSS_LAYERS_ORDER } from '@mynexus/theme';

import { ElementRegistryService } from '@core/service/element-registry.service';
import { MfeRegistryService } from './mfe-registry.service';
import { AuthenticationService } from '@core/authentication/services/authentication.service';
import {
  API_BASE_HH_URL,
  API_BASE_NOTIFICATIONS,
  API_BASE_PLATFORM,
  API_BASE_URL,
  API_BASE_SPECIALTY_PROGRAMS_URL,
} from '../config/config.service';

@Injectable({
  providedIn: 'root'
})
export class SharedService implements ISharedServices {
  authenticationService = inject(AuthenticationService);
  broadcastService = inject(GlobalBroadcastService);
  elementRegistryService = inject(ElementRegistryService);
  router = inject(Router);
  private mfeRegistryService = inject(MfeRegistryService);
  private apiBaseUrl = inject(API_BASE_URL);
  private apiBaseHHUrl = inject(API_BASE_HH_URL);
  private apiBasePlatformUrl = inject(API_BASE_PLATFORM);
  private apiNotifications = inject(API_BASE_NOTIFICATIONS);
  private apiBaseSpecialtyProgramsUrl = inject(API_BASE_SPECIALTY_PROGRAMS_URL);
  private tenantId?: string;

  constructor() {
    this.router.events
      .pipe(
        filter(
          (event): event is ActivationEnd =>
            event instanceof ActivationEnd && Object.keys(event.snapshot.params).length > 0
        ),
        map((event: ActivationEnd) => event.snapshot.params)
      )
      .subscribe(params => {
        this.tenantId = params.tId;
      });
  }

  broadcastMessage(_topic: string, _message: any): void {
    throw new Error('Method not implemented.');
  }

  getMfeService(appName: string): IMfeService {
    return this.mfeRegistryService.getMfeService(appName);
  }

  registerMfeService(appName: string, service: IMfeService): void {
    this.mfeRegistryService.registerMfeService(appName, service);
  }

  getAuthenticationHeader() {
    return {
      name: 'Authorization',
      value: `Bearer ${this.authenticationService.token}`
    };
  }

  /**
   * Determinate current application name based on current url
   */
  getAppName(): any {
    const currentUrl = this.router.url;
    return ProductNames.SDoH;
  }

  getBaseUrlFor(appName: ProductNames = this.getAppName()): string | never {
    if (appName) return this.getBaseApiUrl(appName);

    throw new Error('Unable to detect application name');
  }

  getTenantId(): string {
    return this.tenantId || '';
  }

  getThemeConfiguration(): PrimeNGConfigType {
    return {
      ripple: false,
      theme: {
        preset: Carelon,
        options: {
          darkModeSelector: false,
          cssLayer: {
            name: 'primeng',
            order: CSS_LAYERS_ORDER
          }
        },
      }
    };
  }

  private getBaseApiUrl(appName: ProductNames | string): string {
    const urls: Record<ProductNames | string, string> = {
      [ProductNames.PACIM]: this.apiBaseUrl,
      [ProductNames.PacCommercial]: this.apiBaseUrl,
      [ProductNames.DMEPOS]: this.apiBaseUrl,
      [ProductNames.HomeHealth]: this.apiBaseHHUrl,
      [ProductNames.SDoH]: this.apiBaseUrl,
      [ProductNames.WCC]: this.apiBaseSpecialtyProgramsUrl,
      Platform: this.apiBasePlatformUrl,
      NotificationGateway: this.apiNotifications
    };

    // remove trailing slash
    return urls[appName]?.replace(/\/$/, '');
  }

  // Note: this method is not currently used but is required by the interface.
  // In the future, this method can be used in conjunction with the broadcastMessage method
  // to subscribe to a topic and initiate a callback function when a message is received.
  subscribeToTopic(_topic: string, _callback: MessageCallback): void {
    throw new Error('Method not implemented.');
  }

  unsubscribeFromTopic(_topic: string, _callback: MessageCallback): void {
    throw new Error('Method not implemented.');
  }
}
